<script setup lang="ts">
import type { FormSubmitEvent } from "@nuxt/ui/runtime/types/form.js";
import * as z from "zod";
import TextInput from "./TextInput.vue";
import Button from "../atoms/Button.vue";
import { countries, maxPhoneLengths } from "~/utils/countries";
import { useAnalytics } from "~/composables/useAnalytics";

const props = withDefaults(
  defineProps<{
    artworkId?: string;
    setId?: string;
    artworkUrl?: string;
    buttonVariant?: "primary" | "secondary";
    /** The work shows a price: the button says "I'm Interested!", otherwise "Price on request". */
    hasPrice?: boolean;
  }>(),
  {
    buttonVariant: "primary",
    hasPrice: false,
  }
);

defineOptions({
  inheritAttrs: false,
});

const {
  trackPurchaseCtaClicked,
  trackPurchaseEnquiryStarted,
  trackPurchaseEnquirySubmitted,
  trackFormAbandoned,
} = useAnalytics();

const schema = z.object({
  name: z
    .string("Required")
    .min(2, "Name must be at least 2 characters")
    .max(80, "Name must be under 80 characters")
    .regex(/^[\p{L}\s'\-]+$/u, "Name contains invalid characters"),
  email: z.email("Invalid email").max(254, "Email too long"),
  phoneCountry: z.string(),
  phoneNumber: z.string({ required_error: "Phone number is required" }).min(1, "Phone number is required"),
}).superRefine((data, ctx) => {
  const cleaned = (data.phoneNumber || "").replace(/\D/g, "");
  if (data.phoneCountry === "IN") {
    if (cleaned.length !== 10) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Enter valid 10-digit phone number",
        path: ["phoneNumber"],
      });
    } else if (!/^[6-9]\d{9}$/.test(cleaned)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Enter valid phone number",
        path: ["phoneNumber"],
      });
    }
  } else {
    if (cleaned.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Phone number is required",
        path: ["phoneNumber"],
      });
    } else if (cleaned.length > 10) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Phone number must be up to 10 digits",
        path: ["phoneNumber"],
      });
    }
  }
});

type Schema = z.output<typeof schema>;

const { isLoggedIn, profile, user } = useAuth();

const toast = useToast();
const isOpenModal = ref(false);
const submitted = ref(false);
const submitting = ref(false);
const isDropdownOpen = ref(false);
const triggerRef = ref<HTMLButtonElement | null>(null);
const dropdownRef = ref<HTMLUListElement | null>(null);
const dropdownPos = reactive({ top: 0, left: 0, width: 0 });

const selectedCountry = computed(
  () => countries.find((c) => c.iso === state.phoneCountry) ?? countries[0]
);

function toggleDropdown() {
  if (submitting.value) return;
  if (!isDropdownOpen.value && triggerRef.value) {
    const rect = triggerRef.value.getBoundingClientRect();
    dropdownPos.top = rect.bottom + 4;
    dropdownPos.left = rect.left;
    dropdownPos.width = rect.width;
  }
  isDropdownOpen.value = !isDropdownOpen.value;
}

function selectCountry(iso: string) {
  state.phoneCountry = iso;
  isDropdownOpen.value = false;
}

function closeDropdown() {
  isDropdownOpen.value = false;
}

// Close dropdown when clicking outside the trigger or list
function onDocumentClick(e: MouseEvent) {
  if (!isDropdownOpen.value) return;
  const target = e.target as Node;
  if (triggerRef.value?.contains(target) || dropdownRef.value?.contains(target)) return;
  closeDropdown();
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  window.addEventListener('resize', closeDropdown);
  // Page-level scroll only (bubbles to window); the dropdown's own internal
  // list scroll happens on the <ul> itself and never reaches this listener.
  window.addEventListener('scroll', closeDropdown, { passive: true });
});

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick);
  window.removeEventListener('resize', closeDropdown);
  window.removeEventListener('scroll', closeDropdown);
});

const state = reactive<{
  name?: string;
  email?: string;
  phoneCountry: string;
  phoneNumber?: string;
}>({
  name: undefined,
  email: undefined,
  phoneCountry: "IN",
  phoneNumber: "",
});

function prefillForm() {
  if (isLoggedIn.value) {
    if (user.value?.email) {
      state.email = user.value.email;
    }
    if (profile.value?.name) {
      state.name = profile.value.name;
    }
    if (profile.value?.phone) {
      const parts = profile.value.phone.split("-");
      if (parts.length === 2) {
        const dialCode = parts[0];
        const number = parts[1];
        const foundCountry = countries.find(c => c.dialCode === dialCode);
        if (foundCountry) {
          state.phoneCountry = foundCountry.iso;
        }
        state.phoneNumber = number;
      } else {
        state.phoneNumber = profile.value.phone;
      }
    }
  }
}

onMounted(() => {
  prefillForm();
});

// Re-prefill when profile loads async after mount
watch(() => profile.value, () => {
  prefillForm();
});

// Re-prefill every time the modal opens and track events
watch(isOpenModal, (newVal) => {
  if (newVal) {
    submitted.value = false;
    prefillForm();
    trackPurchaseCtaClicked({
      artwork_id: props.artworkId || props.setId || "",
      cta_label: "Price On Request",
      purchase_path_type: "enquire",
      source_context: "artwork_detail",
    });
    trackPurchaseEnquiryStarted({
      artwork_id: props.artworkId || props.setId,
      form_name: "price_on_request_modal",
      source_context: "artwork_detail",
    });
  } else if (!submitted.value) {
    trackFormAbandoned({
      form_name: "price_on_request_modal",
      artwork_id: props.artworkId || props.setId,
    });
  }
});


function getMaxLength(iso: string): number {
  return 10;
}

// Intercept and sanitize inputs in real-time to keep digits only and respect country limits
function handlePhoneInput(e: Event) {
  const input = e.target as HTMLInputElement;
  let cleaned = input.value.replace(/\D/g, "");
  const max = getMaxLength(state.phoneCountry);
  if (cleaned.length > max) {
    cleaned = cleaned.substring(0, max);
  }
  state.phoneNumber = cleaned;
  input.value = cleaned;
}

// Watch country change to truncate any existing digits exceeding new country's limit
watch(() => state.phoneCountry, (newCountry) => {
  if (state.phoneNumber) {
    const max = getMaxLength(newCountry);
    if (state.phoneNumber.length > max) {
      state.phoneNumber = state.phoneNumber.substring(0, max);
    }
  }
});

async function onSubmit(event: FormSubmitEvent<Schema>) {
  const {
    data: { name, email, phoneCountry, phoneNumber },
  } = event;

  submitting.value = true;

  let formattedPhone: string | null = null;
  if (phoneNumber && phoneNumber.trim().length > 0) {
    const selected = countries.find((c) => c.iso === phoneCountry);
    let cleaned = phoneNumber.replace(/\D/g, "");
    if (selected) {
      const prefixDigits = selected.dialCode.replace(/\D/g, "");
      if (cleaned.startsWith(prefixDigits) && cleaned.length > prefixDigits.length) {
        cleaned = cleaned.substring(prefixDigits.length);
      }
      formattedPhone = `${selected.dialCode}-${cleaned}`;
    } else {
      formattedPhone = `${phoneCountry}-${cleaned}`;
    }
  }

  try {
    await $fetch("/api/price-on-request", {
      method: "POST",
      body: {
        artwork_id: props.artworkId,
        set_id: props.setId ? Number(props.setId) : undefined,
        artwork_url: props.artworkUrl,
        email,
        name,
        phone: formattedPhone,
      },
    });

    toast.add({
      title: "Request submitted!",
      color: "success",
    });

    submitted.value = true;
    trackPurchaseEnquirySubmitted({
      artwork_id: props.artworkId || props.setId,
      form_name: "price_on_request_modal",
      source_context: "artwork_detail",
    });
    isOpenModal.value = false;
  } catch {
    toast.add({
      title: "Could not send price request, please try again.",
      color: "error",
    });
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div v-bind="$attrs" class="flex flex-col gap-2 w-full">
    <div v-if="submitted" class="text-base font-normal text-on-primary-body mb-2">
      Thank you for showing your interest!<br />We will reach out to you soon with
      further details.
    </div>

    <div class="flex flex-col sm:flex-row xl:flex-nowrap flex-wrap items-stretch gap-2 md:gap-3 w-full">
      <Button
        v-if="!submitted"
        :variant="buttonVariant"
        @click="isOpenModal = true"
        class="cursor-pointer tracking-wide w-full sm:flex-1 sm:min-w-[calc(50%-0.5rem)] xl:min-w-0 xl:flex-1 justify-center whitespace-nowrap px-3 md:px-4 text-xs sm:text-[13px] md:text-sm"
      >
        {{ hasPrice ? "I'm Interested!" : "Price on request" }}
      </Button>
      <slot name="actions" />
    </div>

  </div>

  <!-- Modal Form Pop-up -->
  <UModal
    v-model:open="isOpenModal"
      title="I'm Interested"
      close-icon="lucide:x"
      :ui="{
        content: 'rounded-[6px] shadow-none border border-on-primary-heading bg-white p-6 max-w-lg w-full',
        close: 'cursor-pointer',
      }"
    >
      <template #body>
        <UForm
          :schema="schema"
          :state="state"
          class="w-full space-y-5 mt-4"
          @submit="onSubmit"
        >
          <!-- Name -->
          <UFormField name="name" label="Name" class="w-full">
            <template #default="{ error }">
              <TextInput
                placeholder="Your name"
                name="name"
                v-model="state.name"
                :disabled="submitting"
                maxlength="80"
                autocomplete="name"
                :error="error"
              />
            </template>
          </UFormField>

          <!-- Email -->
          <UFormField name="email" label="Email" class="w-full">
            <template #default="{ error }">
              <TextInput
                placeholder="example@email.com"
                name="email"
                type="email"
                v-model="state.email"
                :disabled="submitting"
                maxlength="255"
                autocomplete="email"
                :error="error"
              />
            </template>
          </UFormField>

          <!-- Phone Number -->
          <UFormField name="phoneNumber" label="Phone Number" class="w-full">
            <template #default="{ error }">
              <div class="flex items-stretch gap-2 w-full">
                <!-- Country code custom dropdown -->
                <div class="shrink-0 relative">
                  <!-- Trigger button: shows flag + dial code only -->
                  <button
                    ref="triggerRef"
                    type="button"
                    :disabled="submitting"
                    @click="toggleDropdown"
                    class="flex items-center gap-1.5 h-full w-full rounded-[6px] border-0 px-3 py-3 text-sm font-medium text-on-primary-heading ring ring-inset ring-on-primary-heading transition-colors outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-75 bg-white dark:bg-black whitespace-nowrap"
                  >
                    <span>{{ selectedCountry.flag }} {{ selectedCountry.dialCode }}</span>
                    <svg class="w-3.5 h-3.5 ml-1 shrink-0 text-on-primary-heading/60 transition-transform" :class="{ 'rotate-180': isDropdownOpen }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <!-- Dropdown teleported to body so it renders outside the modal -->
                  <Teleport to="body">
                    <ul
                      ref="dropdownRef"
                      v-if="isDropdownOpen"
                      :style="{
                        position: 'fixed',
                        top: dropdownPos.top + 'px',
                        left: dropdownPos.left + 'px',
                        minWidth: dropdownPos.width + 'px',
                        maxHeight: '260px',
                        overflowY: 'auto',
                        zIndex: 99999,
                      }"
                      class="bg-white dark:bg-black border border-on-primary-heading/20 rounded-[6px] shadow-xl w-max pointer-events-auto"
                      @pointerdown.stop
                    >
                      <li
                        v-for="c in countries"
                        :key="c.iso"
                        @click="selectCountry(c.iso)"
                        class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                        :class="{ 'bg-on-primary-heading text-white hover:bg-on-primary-heading': c.iso === state.phoneCountry }"
                      >
                        <span>{{ c.flag }}</span>
                        <span class="font-medium">{{ c.name }}</span>
                        <span class="ml-auto pl-3">{{ c.dialCode }}</span>
                      </li>
                    </ul>
                  </Teleport>
                </div>
                <!-- Phone input -->
                <div class="flex-1">
                  <TextInput
                    name="phoneNumber"
                    type="tel"
                    :placeholder="countries.find(c => c.iso === state.phoneCountry) ? countries.find(c => c.iso === state.phoneCountry)!.name + ' phone number' : 'Phone number'"
                    v-model="state.phoneNumber"
                    :disabled="submitting"
                    :maxlength="getMaxLength(state.phoneCountry)"
                    @input="handlePhoneInput"
                    :error="error"
                  />
                </div>
              </div>
            </template>
          </UFormField>

          <!-- Footer Actions -->
          <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <Button
              type="button"
              variant="secondary"
              :disabled="submitting"
              @click="isOpenModal = false"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              :loading="submitting"
              :disabled="submitting"
            >
              Submit
            </Button>
          </div>
        </UForm>
      </template>
    </UModal>
</template>
